import React, { useEffect, useMemo, useRef, useState } from 'react';

const Overscan = 4;

const useResizeObserver = (ref, onResize) => {
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        onResize(entry.contentRect.height);
      });
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, onResize]);
};

function MeasuredRow({ top, onHeight, children }) {
  const ref = useRef(null);
  useResizeObserver(ref, onHeight);
  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top,
        left: 0,
        right: 0,
        paddingBottom: 12
      }}
    >
      {children}
    </div>
  );
}

export default function VirtualisedList({
  items,
  renderItem,
  estimatedItemHeight = 120,
  height = 520
}) {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [itemHeights, setItemHeights] = useState({});

  const heights = useMemo(
    () => items.map((_, idx) => itemHeights[idx] || estimatedItemHeight),
    [items, itemHeights, estimatedItemHeight]
  );

  const offsets = useMemo(() => {
    let acc = 0;
    return heights.map((height) => {
      const current = acc;
      acc += height;
      return current;
    });
  }, [heights]);

  const totalHeight = offsets.length ? offsets[offsets.length - 1] + heights[heights.length - 1] : 0;

  const onScroll = (event) => {
    setScrollTop(event.currentTarget.scrollTop);
  };

  const { startIndex, endIndex } = useMemo(() => {
    let start = 0;
    while (start < offsets.length && offsets[start] + heights[start] < scrollTop) {
      start += 1;
    }
    start = Math.max(0, start - Overscan);
    let end = start;
    const max = scrollTop + height;
    while (end < offsets.length && offsets[end] < max) {
      end += 1;
    }
    end = Math.min(offsets.length, end + Overscan);
    return { startIndex: start, endIndex: end };
  }, [scrollTop, offsets, heights, height]);

  const visibleItems = items.slice(startIndex, endIndex);

  return (
    <div
      ref={containerRef}
      style={{ height, overflowY: 'auto', position: 'relative' }}
      onScroll={onScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        {visibleItems.map((item, idx) => {
          const index = startIndex + idx;
          const top = offsets[index];
          return (
            <MeasuredRow
              key={item.id || index}
              top={top}
              onHeight={(nextHeight) => {
                setItemHeights((prev) => {
                  if (prev[index] === nextHeight) return prev;
                  return { ...prev, [index]: nextHeight };
                });
              }}
            >
              {renderItem(item, index)}
            </MeasuredRow>
          );
        })}
      </div>
    </div>
  );
}
